import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Search,
  RefreshCw,
  Filter,
  AlertTriangle,
  FileText,
  ChevronUp,
  ChevronDown,
  Calendar,
  Building2,
  Scale,
  Users,
  Trash2,
  CheckSquare,
  Square,
  ArrowRight,
  X,
  Archive,
  Ban,
  RotateCcw,
} from "lucide-react";
import Pagination from "../components/common/Pagination";
import PerPageSelector from "../components/common/PerPageSelector";
import PublicationDjeDetailsSidebar from "../components/publications-dje/PublicationDjeDetailsSidebar";
import FilterSidebarDje from "../components/publications-dje/FilterSidebarDje";
import ProcessNumber from "../components/common/ProcessNumber";
import CNJSearchInput from "../components/common/CNJSearchInput";
import QuickLawyerPartnerModal from "../components/lawyer-partners/QuickLawyerPartnerModal";
import { cleanCNJForSearch, isCNJNumber } from "../hooks/useSearchCNJ";
import { publicationsDjeService } from "../services";
import { processesService } from "../services/processesService";
import PartnerLawyerBadge from "../components/common/PartnerLawyerBadge";
import ConvertedToProcessBadge from "../components/common/ConvertedToProcessBadge";
import { lawyerPartnersService } from "../services/lawyerPartnersService";
import {
  extractActiveLawyerCandidates,
  filterDisplayPoles,
  findMatchingLawyerPartners,
} from "../utils/lawyerPartners";
import { getScoreBadgeStyles } from "../utils/scoreClassification";

export default function PublicacoesDjePage() {
  const toYMDLocal = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const getDefaultRecentDateRange = () => {
    const end = new Date();
    const start = new Date();
    // últimos 3 dias (inclui hoje): hoje + 2 dias anteriores
    start.setDate(end.getDate() - 2);
    return { start: toYMDLocal(start), end: toYMDLocal(end) };
  };

  const defaultRecentDateRange = getDefaultRecentDateRange();

  // URL params
  const [searchParams, setSearchParams] = useSearchParams();

  // Estados dos dados
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estados da paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Estados de interface
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("novo");
  const [selectedClassifications, setSelectedClassifications] = useState([]);
  const [availableClassifications, setAvailableClassifications] = useState([]);
  const [classificationsLoading, setClassificationsLoading] = useState(false);
  const [selectedScoreRange, setSelectedScoreRange] = useState("");
  const [selectedTribunal, setSelectedTribunal] = useState("");
  const [availableTribunals, setAvailableTribunals] = useState([]);
  const [tribunalsLoading, setTribunalsLoading] = useState(false);
  const [selectedJusticeSphere, setSelectedJusticeSphere] = useState("");
  const [availableJusticeSpheres, setAvailableJusticeSpheres] = useState([]);
  const [justiceSpheresLoading, setJusticeSpheresLoading] = useState(false);
  const [selectedState, setSelectedState] = useState("");
  const [availableStates, setAvailableStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [selectedPoloPassivo, setSelectedPoloPassivo] = useState("");
  const [selectedDateStart, setSelectedDateStart] = useState(
    defaultRecentDateRange.start,
  );
  const [selectedDateEnd, setSelectedDateEnd] = useState(
    defaultRecentDateRange.end,
  );
  const [selectedPublicacao, setSelectedPublicacao] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(false);
  const [lawyerPartners, setLawyerPartners] = useState([]);
  const [quickLawyerModalOpen, setQuickLawyerModalOpen] = useState(false);
  const [quickLawyerSource, setQuickLawyerSource] = useState(null);

  // Seleção múltipla para envio em lote de processos
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [isBulkConverting, setIsBulkConverting] = useState(false);
  const [isBulkArchiving, setIsBulkArchiving] = useState(false);
  const [isBulkUnarchiving, setIsBulkUnarchiving] = useState(false);
  const [isBulkDiscarding, setIsBulkDiscarding] = useState(false);
  const [isBulkRestoring, setIsBulkRestoring] = useState(false);

  const loadLawyerPartners = async () => {
    try {
      const data = await lawyerPartnersService.getPartners();
      setLawyerPartners(data || []);
    } catch (error) {
      setLawyerPartners([]);
    }
  };

  // Opções de filtro
  const filtros = [
    { id: "novo", label: "Novas" },
    { id: "arquivado", label: "Arquivadas" },
    { id: "descartado", label: "Descartadas" },
    { id: "todas", label: "Todas" },
  ];

  // Estados de ordenação
  const [sortField, setSortField] = useState("datePublication");
  const [sortDirection, setSortDirection] = useState("desc");

  // Função de ordenação
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection(
        field === "score" || field === "datePublication" ? "desc" : "asc",
      );
    }
  };

  // Ícone de ordenação
  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <ChevronUp className="w-3 h-3 ml-1 inline" />
    ) : (
      <ChevronDown className="w-3 h-3 ml-1 inline" />
    );
  };

  // A classificação entra na busca só em Aplicar Filtros. Recarregar a cada
  // checkbox deixava a lista lenta e a marcação parecia não responder.
  useEffect(() => {
    fetchPublications();
  }, [
    currentPage,
    itemsPerPage,
    searchTerm,
    selectedFilter,
    selectedScoreRange,
    selectedTribunal,
    selectedJusticeSphere,
    selectedState,
    selectedPoloPassivo,
    selectedDateStart,
    selectedDateEnd,
    sortField,
    sortDirection,
  ]);

  // Carregar filtros disponíveis
  useEffect(() => {
    loadLawyerPartners();
    fetchClassifications();
    fetchTribunals();
    fetchJusticeSpheres();
    fetchStates();
  }, []);

  // Busca por CNJ via query string (ex.: link a partir de ProcessoDetalhes)
  useEffect(() => {
    const urlSearchTerm = searchParams.get("searchTerm");
    if (urlSearchTerm && urlSearchTerm.trim() !== "") {
      setSearchTerm(urlSearchTerm.trim());
      setCurrentPage(1);
    }
  }, [searchParams]);

  // Handle URL parameter to open publication details
  useEffect(() => {
    const publicationId = searchParams.get("id");
    if (!publicationId || publications.length === 0 || sidebarOpen) {
      return;
    }

    const publicacao = publicacoesData.find((pub) => pub.id === publicationId);
    if (publicacao) {
      handleViewDetails(publicacao);
    }
  }, [publications, searchParams, sidebarOpen]);

  const isSearchingByCNJ = isCNJNumber(searchTerm);

  const fetchPublications = async () => {
    try {
      setLoading(true);
      setError(null);

      const cleanedSearchTerm = cleanCNJForSearch(searchTerm);
      const searchingByCNJ = isCNJNumber(searchTerm);

      const params = searchingByCNJ
        ? {
            page: currentPage,
            limit: itemsPerPage,
            searchTerm: cleanedSearchTerm,
            sortBy: sortField,
            sortOrder: sortDirection,
          }
        : {
            page: currentPage,
            limit: itemsPerPage,
            searchTerm: cleanedSearchTerm,
            sortBy: sortField,
            sortOrder: sortDirection,
            classification: selectedClassifications.join(","),
            tribunal: selectedTribunal,
            scoreRange: selectedScoreRange,
            dailySphere: selectedJusticeSphere,
            dailyState: selectedState,
            poloPassivo: selectedPoloPassivo,
            dateStart: selectedDateStart,
            dateEnd: selectedDateEnd,
            includeArchived:
              selectedFilter === "todas" ||
              selectedFilter === "arquivado" ||
              selectedFilter === "descartado",
            newsPublications: selectedFilter === "novo",
            onlyArchived: selectedFilter === "arquivado",
            onlyDiscarded: selectedFilter === "descartado",
          };

      const response = await publicationsDjeService.getPublications(params);

      setPublications(response.data || []);
      setTotalItems(response.meta?.total || 0);
      setTotalPages(response.meta?.totalPages || 1);
      setSelectedIds(new Set());
    } catch (err) {
      setError("Erro ao carregar publicações DJE");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPublications();
  };

  const handleRefresh = () => {
    fetchPublications();
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchPublications();
  };

  const isDefaultDateRange = (start, end) =>
    start === defaultRecentDateRange.start && end === defaultRecentDateRange.end;

  const hasNonSearchFilters = () =>
    selectedFilter !== "novo" ||
    selectedClassifications.length > 0 ||
    selectedScoreRange !== "" ||
    selectedTribunal !== "" ||
    selectedJusticeSphere !== "" ||
    selectedState !== "" ||
    selectedPoloPassivo.trim() !== "" ||
    !isDefaultDateRange(selectedDateStart, selectedDateEnd);

  const hasActiveFilters = () =>
    searchTerm.trim() !== "" || hasNonSearchFilters();

  const countSidebarFilters = () =>
    selectedClassifications.length +
    (selectedScoreRange ? 1 : 0) +
    (selectedTribunal ? 1 : 0) +
    (selectedJusticeSphere ? 1 : 0) +
    (selectedState ? 1 : 0) +
    (selectedPoloPassivo.trim() ? 1 : 0) +
    (!isDefaultDateRange(selectedDateStart, selectedDateEnd) ? 1 : 0);

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedFilter("novo");
    setSelectedClassifications([]);
    setSelectedScoreRange("");
    setSelectedTribunal("");
    setSelectedJusticeSphere("");
    setSelectedState("");
    setSelectedPoloPassivo("");
    // Ao limpar filtros, volta para o padrão de "mais recentes"
    setSelectedDateStart(defaultRecentDateRange.start);
    setSelectedDateEnd(defaultRecentDateRange.end);
    setCurrentPage(1);

    if (searchParams.has("searchTerm")) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("searchTerm");
      setSearchParams(nextParams);
    }
    // useEffect reage às mudanças de estado acima — não chamar fetchPublications aqui
    // (setTimeout reexecutava com estado antigo e reaplicava os filtros limpos)
  };

  const fetchClassifications = async () => {
    try {
      setClassificationsLoading(true);
      const response =
        await publicationsDjeService.getPublicationsClassifications();
      setAvailableClassifications(response || []);
    } catch (error) {
      setAvailableClassifications([]);
    } finally {
      setClassificationsLoading(false);
    }
  };

  const fetchTribunals = async () => {
    try {
      setTribunalsLoading(true);
      const data = await publicationsDjeService.getTribunals();
      setAvailableTribunals(data || []);
    } catch (error) {
      setAvailableTribunals([]);
    } finally {
      setTribunalsLoading(false);
    }
  };

  const fetchJusticeSpheres = async () => {
    try {
      setJusticeSpheresLoading(true);
      const data = await publicationsDjeService.getJusticeSpheres();
      setAvailableJusticeSpheres(data || []);
    } catch (error) {
      setAvailableJusticeSpheres([]);
    } finally {
      setJusticeSpheresLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      setStatesLoading(true);
      const data = await publicationsDjeService.getStates();
      setAvailableStates(data || []);
    } catch (error) {
      setAvailableStates([]);
    } finally {
      setStatesLoading(false);
    }
  };

  // Transformar dados reais da API para o formato da interface
  const publicacoesData = publications.map((pub) => {
    const conteudo = pub.texto || pub.conteudo || "";

    return {
      id: pub.id,
      numeroProcesso: pub.numeroProcesso || pub.id,
      numeroProcessoFormatted:
        pub.numeroProcessoFormatted || pub.numeroProcesso,
      tribunal: pub.tribunal || "N/A",
      nomeOrgao: pub.nomeOrgao || "Órgão não informado",
      tipoComunicacao: pub.tipoComunicacao || "Não informado",
      tipoDocumento: pub.tipoDocumento || "Não informado",
      nomeClasse: pub.nomeClasse,
      codigoClasse: pub.codigoClasse,
      dataDisponibilizacao: pub.dataDisponibilizacao
        ? new Date(pub.dataDisponibilizacao).toLocaleDateString("pt-BR", {
            timeZone: "UTC",
          })
        : "N/A",
      datadisponibilizacao: pub.datadisponibilizacao || "",
      score: pub.score || 0,
      scoreBreakdown: pub.scoreBreakdown || null,
      convertedToProcess: Boolean(pub.convertedToProcess),
      processId: pub.processId || null,
      processStatus: pub.processStatus || null,
      arquivada: Boolean(pub.arquivada),
      descartada: Boolean(pub.descartada),
      highestValue: pub.highestValue || null,
      monetaryValues: pub.monetaryValues || null,
      totalMonetaryValues: pub.totalMonetaryValues || 0,
      valuesExtractedAt: pub.valuesExtractedAt || null,
      classifications: pub.classifications || [],
      parties: pub.parties || { activePoles: [], passivePoles: [] },
      displayParties: filterDisplayPoles(
        pub.parties || { activePoles: [], passivePoles: [] },
      ),
      lawyerCandidates: extractActiveLawyerCandidates(
        pub.parties || { activePoles: [], passivePoles: [] },
      ),
      matchedPartners: findMatchingLawyerPartners({
        partners: lawyerPartners,
        lawyers: extractActiveLawyerCandidates(
          pub.parties || { activePoles: [], passivePoles: [] },
        ),
      }),
      detalhes: {
        externalId: pub.externalId,
        numeroProcesso: pub.numeroProcesso,
        numeroProcessoFormatted: pub.numeroProcessoFormatted,
        tribunal: pub.tribunal,
        nomeOrgao: pub.nomeOrgao,
        tipoComunicacao: pub.tipoComunicacao,
        tipoDocumento: pub.tipoDocumento,
        nomeClasse: pub.nomeClasse,
        codigoClasse: pub.codigoClasse,
        numeroComunicacao: pub.numeroComunicacao,
        meio: pub.meio,
        link: pub.link,
        conteudo,
        hash: pub.hash,
        status: pub.status,
        ativo: pub.ativo,
        arquivada: pub.arquivada,
        descartada: pub.descartada,
        resumo: pub.resumo,
        gptProcessedAt: pub.gptProcessedAt,
        gptTokensUsed: pub.gptTokensUsed,
        validationResult: pub.validationResult,
        processedAt: pub.processedAt,
        createdAt: pub.createdAt,
        updatedAt: pub.updatedAt,
      },
    };
  });

  const getScoreBadge = (score, scoreBreakdown) => {
    if (score === null || score === undefined) {
      return (
        <div className="flex flex-col items-center gap-1 min-w-[70px]">
          <div className="px-3 py-1.5 bg-gray-100 rounded-lg">
            <span className="text-lg font-bold text-gray-400">--</span>
          </div>
          <span className="text-xs font-medium text-gray-500 uppercase">
            N/A
          </span>
        </div>
      );
    }

    const { classification, colorClass, bgClass } = getScoreBadgeStyles(score);

    return (
      <div className="flex flex-col items-center gap-1.5 min-w-[100px]">
        <div
          className={`px-3 py-1.5 ${bgClass} rounded-lg border-2 ${
            colorClass.split(" ")[2]
          }`}
        >
          <span className={`text-2xl font-bold ${colorClass.split(" ")[1]}`}>
            {score}
          </span>
        </div>
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border ${colorClass}`}
        >
          {classification}
        </span>
      </div>
    );
  };

  const filteredPublicacoes = publicacoesData;

  const handleViewDetails = async (publicacao) => {
    try {
      setDetailsLoading(true);
      setSearchParams({ id: publicacao.id });

      const detailedPublication =
        await publicationsDjeService.getPublicationById(publicacao.id);

      const detailedContent =
        detailedPublication.texto || publicacao.detalhes?.conteudo || "";
      const detailedParties = detailedPublication.parties || publicacao.parties;
      const lawyerCandidates = extractActiveLawyerCandidates(detailedParties);
      const enrichedPublication = {
        ...publicacao,
        resumoIa: detailedPublication.resumoIa,
        resumoIaGeneratedAt: detailedPublication.resumoIaGeneratedAt,
        matchedPartners: findMatchingLawyerPartners({
          partners: lawyerPartners,
          lawyers: lawyerCandidates,
        }),
        detalhes: {
          ...publicacao.detalhes,
          conteudo: detailedContent,
          ...detailedPublication,
        },
        lida: detailedPublication.lida,
        classifications:
          detailedPublication.classifications || publicacao.classifications,
        parties: detailedParties,
        displayParties: filterDisplayPoles(detailedParties),
        lawyerCandidates,
        scoreBreakdown:
          detailedPublication.scoreBreakdown || publicacao.scoreBreakdown,
        convertedToProcess: Boolean(
          detailedPublication.convertedToProcess ?? publicacao.convertedToProcess,
        ),
        processId: detailedPublication.processId ?? publicacao.processId ?? null,
        processStatus:
          detailedPublication.processStatus ?? publicacao.processStatus ?? null,
        arquivada: Boolean(
          detailedPublication.arquivada ?? publicacao.arquivada,
        ),
        descartada: Boolean(
          detailedPublication.descartada ?? publicacao.descartada,
        ),
      };

      setSelectedPublicacao(enrichedPublication);
      setSidebarOpen(true);
    } catch (error) {
      setSelectedPublicacao(publicacao);
      setSidebarOpen(true);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handlePartnerCreated = async (createdPartner) => {
    if (!createdPartner) return;
    await loadLawyerPartners();
    setQuickLawyerModalOpen(false);
    setQuickLawyerSource(null);

    setSelectedPublicacao((current) => {
      if (!current) return current;
      const nextPartners = [createdPartner, ...(current.matchedPartners || [])];
      return {
        ...current,
        matchedPartners: nextPartners.filter(
          (partner, index, array) =>
            array.findIndex((item) => item.id === partner.id) === index,
        ),
      };
    });
  };

  const openQuickLawyerModal = (publication, lawyerCandidate) => {
    setQuickLawyerSource({ publication, lawyerCandidate });
    setQuickLawyerModalOpen(true);
  };

  const handlePublicationConverted = (publicationId, conversionInfo) => {
    const applyConversion = (item) =>
      item?.id === publicationId
        ? {
            ...item,
            convertedToProcess: true,
            processId: conversionInfo.processId,
            processStatus: conversionInfo.processStatus ?? "AGUARDANDO_ANALISE",
          }
        : item;

    setPublications((current) => current.map(applyConversion));
    setSelectedPublicacao((current) => applyConversion(current));
  };

  const handlePublicationReclassified = (updatedPublication) => {
    const applyReclassification = (item) => {
      if (!item || item.id !== updatedPublication.id) return item;

      return {
        ...item,
        score: updatedPublication.score ?? item.score,
        scoreBreakdown:
          updatedPublication.scoreBreakdown ?? item.scoreBreakdown,
        arquivada: updatedPublication.arquivada ?? item.arquivada,
        classifications:
          updatedPublication.classifications ?? item.classifications,
        parties: updatedPublication.parties ?? item.parties,
        displayParties: filterDisplayPoles(
          updatedPublication.parties ?? item.parties,
        ),
        detalhes: {
          ...item.detalhes,
          arquivada: updatedPublication.arquivada ?? item.detalhes?.arquivada,
        },
      };
    };

    setPublications((current) => current.map(applyReclassification));
    setSelectedPublicacao((current) => applyReclassification(current));
  };

  const matchesArchivedPublication = (item, publicationId, numeroProcesso) => {
    if (item.id === publicationId) return true;
    if (!numeroProcesso) return false;

    const itemProcess =
      item.numeroProcesso ||
      item.detalhes?.numeroProcesso ||
      item.detalhes?.numero_processo;

    return itemProcess === numeroProcesso;
  };

  const handlePublicationArchived = (publicationId, numeroProcesso) => {
    if (selectedFilter !== "arquivado") {
      setPublications((current) =>
        current.filter(
          (item) =>
            !matchesArchivedPublication(item, publicationId, numeroProcesso),
        ),
      );
    } else {
      setPublications((current) =>
        current.map((item) =>
          matchesArchivedPublication(item, publicationId, numeroProcesso)
            ? { ...item, arquivada: true }
            : item,
        ),
      );
    }

    closePublicationSidebar();
  };

  const handlePublicationUnarchived = (publicationId, numeroProcesso) => {
    if (selectedFilter === "arquivado") {
      setPublications((current) =>
        current.filter(
          (item) =>
            !matchesArchivedPublication(item, publicationId, numeroProcesso),
        ),
      );
    } else {
      setPublications((current) =>
        current.map((item) =>
          matchesArchivedPublication(item, publicationId, numeroProcesso)
            ? { ...item, arquivada: false }
            : item,
        ),
      );
    }

    closePublicationSidebar();
  };

  const handlePublicationDiscarded = (publicationId, numeroProcesso) => {
    if (selectedFilter === "descartado") {
      setPublications((current) =>
        current.map((item) =>
          matchesArchivedPublication(item, publicationId, numeroProcesso)
            ? { ...item, arquivada: true, descartada: true }
            : item,
        ),
      );
    } else {
      setPublications((current) =>
        current.filter(
          (item) =>
            !matchesArchivedPublication(item, publicationId, numeroProcesso),
        ),
      );
    }

    closePublicationSidebar();
  };

  const handlePublicationRestored = (publicationId, numeroProcesso) => {
    if (selectedFilter === "descartado") {
      setPublications((current) =>
        current.filter(
          (item) =>
            !matchesArchivedPublication(item, publicationId, numeroProcesso),
        ),
      );
    } else {
      setPublications((current) =>
        current.map((item) =>
          matchesArchivedPublication(item, publicationId, numeroProcesso)
            ? { ...item, arquivada: false, descartada: false }
            : item,
        ),
      );
    }

    closePublicationSidebar();
  };

  // Selecionável para conversão: não convertida + CNJ.
  // Nas abas Arquivadas/Descartadas: qualquer publicação com CNJ (desarquivar/reabilitar).
  const isSelectablePublicacao = (publicacao) => {
    const hasCnj = Boolean(
      publicacao.detalhes?.numeroProcesso || publicacao.numeroProcesso,
    );
    if (selectedFilter === "descartado" || selectedFilter === "arquivado") {
      return hasCnj;
    }
    return !publicacao.convertedToProcess && hasCnj;
  };

  const selectablePublicacoes = filteredPublicacoes.filter(
    isSelectablePublicacao,
  );

  const allSelected =
    selectablePublicacoes.length > 0 &&
    selectablePublicacoes.every((publicacao) => selectedIds.has(publicacao.id));

  const toggleSelectPublicacao = (publicacaoId) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(publicacaoId)) {
        next.delete(publicacaoId);
      } else {
        next.add(publicacaoId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((current) => {
      if (
        selectablePublicacoes.length > 0 &&
        selectablePublicacoes.every((publicacao) => current.has(publicacao.id))
      ) {
        return new Set();
      }
      return new Set(selectablePublicacoes.map((publicacao) => publicacao.id));
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const buildProcessDataFromPublicacao = (publicacao) => ({
    publicationId: publicacao.id,
    numeroProcesso: publicacao.detalhes?.numeroProcesso,
    tribunal: publicacao.detalhes?.tribunal || publicacao.tribunal || undefined,
    esfera: publicacao.detalhes?.esfera || undefined,
    estado: publicacao.detalhes?.estado || undefined,
    classificacoes: publicacao.classifications?.map((c) => c.name) || [],
    valorEstimado: publicacao.highestValue || null,
    dataPublicacao: publicacao.datadisponibilizacao || undefined,
    resumo: publicacao.detalhes?.resumo || undefined,
    conteudo: publicacao.detalhes?.conteudo || undefined,
    score: publicacao.score || 0,
    status: "AGUARDANDO_ANALISE",
  });

  const handleBulkConvert = async () => {
    if (isBulkConverting || selectedIds.size === 0) return;

    const selecionadas = filteredPublicacoes.filter(
      (publicacao) =>
        selectedIds.has(publicacao.id) && isSelectablePublicacao(publicacao),
    );

    if (selecionadas.length === 0) {
      alert("Nenhuma publicação válida selecionada para conversão.");
      return;
    }

    const confirmado = window.confirm(
      `Converter ${selecionadas.length} publicação(ões) em processo(s)?`,
    );
    if (!confirmado) return;

    setIsBulkConverting(true);
    try {
      const items = selecionadas.map(buildProcessDataFromPublicacao);
      const result = await processesService.bulkCreateProcesses(items);

      const createdList = result?.created || [];
      createdList.forEach((entry) => {
        if (entry.publicationId) {
          handlePublicationConverted(entry.publicationId, {
            processId: entry.processId,
            processStatus: entry.status,
          });
        }
      });

      clearSelection();

      const createdCount = result?.createdCount ?? createdList.length;
      const failedCount = result?.failedCount ?? 0;
      let message = `${createdCount} processo(s) criado(s) com sucesso.`;
      if (failedCount > 0) {
        const detalhes = (result?.failed || [])
          .map(
            (f) =>
              `• ${f.numeroProcesso || f.publicationId || "—"}: ${f.error}`,
          )
          .join("\n");
        message += `\n\n${failedCount} não convertida(s):\n${detalhes}`;
      }
      alert(message);
    } catch (error) {
      const apiMessage = error?.response?.data?.error;
      alert(
        apiMessage ||
          "Erro ao converter publicações em processos. Tente novamente.",
      );
    } finally {
      setIsBulkConverting(false);
    }
  };

  // Remove da lista as publicações processadas nas abas de status especializadas.
  const removeSelectedFromList = (ids) => {
    const idSet = new Set(ids);
    if (selectedFilter === "arquivado" || selectedFilter === "descartado") {
      setPublications((current) =>
        current.filter((item) => !idSet.has(item.id)),
      );
    } else {
      setPublications((current) =>
        current.filter((item) => !idSet.has(item.id)),
      );
    }
  };

  const isBulkBusy =
    isBulkConverting ||
    isBulkArchiving ||
    isBulkUnarchiving ||
    isBulkDiscarding ||
    isBulkRestoring;

  const handleBulkArchive = async () => {
    if (isBulkBusy || selectedIds.size === 0) return;

    const ids = Array.from(selectedIds);
    const confirmado = window.confirm(
      `Arquivar ${ids.length} publicação(ões) selecionada(s)? Todas as comunicações dos mesmos processos também serão arquivadas.`,
    );
    if (!confirmado) return;

    setIsBulkArchiving(true);
    try {
      const result = await publicationsDjeService.bulkArchive(ids);
      const processedIds = result?.data?.processedIds || ids;

      removeSelectedFromList(processedIds);
      clearSelection();
      closePublicationSidebar();

      alert(
        `${processedIds.length} publicação(ões) arquivada(s) com sucesso.`,
      );
    } catch (error) {
      const apiMessage = error?.response?.data?.error;
      alert(apiMessage || "Erro ao arquivar publicações. Tente novamente.");
    } finally {
      setIsBulkArchiving(false);
    }
  };

  const handleBulkUnarchive = async () => {
    if (isBulkBusy || selectedIds.size === 0) return;

    const ids = Array.from(selectedIds);
    const confirmado = window.confirm(
      `Desarquivar ${ids.length} publicação(ões) selecionada(s)? Elas voltarão à listagem de novas.`,
    );
    if (!confirmado) return;

    setIsBulkUnarchiving(true);
    try {
      const result = await publicationsDjeService.bulkUnarchive(ids);
      const processedIds = result?.data?.processedIds || ids;

      removeSelectedFromList(processedIds);
      clearSelection();
      closePublicationSidebar();

      alert(
        `${processedIds.length} publicação(ões) desarquivada(s) com sucesso.`,
      );
    } catch (error) {
      const apiMessage = error?.response?.data?.error;
      alert(apiMessage || "Erro ao desarquivar publicações. Tente novamente.");
    } finally {
      setIsBulkUnarchiving(false);
    }
  };

  const handleBulkDiscard = async () => {
    if (isBulkBusy || selectedIds.size === 0) return;

    const ids = Array.from(selectedIds);
    const confirmado = window.confirm(
      `Descartar ${ids.length} publicação(ões) selecionada(s)?\n\nAo descartar, os processos serão arquivados e NENHUMA nova atualização desses casos será sincronizada novamente.`,
    );
    if (!confirmado) return;

    setIsBulkDiscarding(true);
    try {
      const result = await publicationsDjeService.bulkDiscard(ids);
      const processedIds = result?.data?.processedIds || ids;
      const discardedCount =
        result?.data?.discardedProcesses?.length ?? processedIds.length;

      removeSelectedFromList(processedIds);
      clearSelection();
      closePublicationSidebar();

      alert(
        `${processedIds.length} publicação(ões) descartada(s). ${discardedCount} processo(s) não voltarão a ser sincronizados.`,
      );
    } catch (error) {
      const apiMessage = error?.response?.data?.error;
      alert(apiMessage || "Erro ao descartar publicações. Tente novamente.");
    } finally {
      setIsBulkDiscarding(false);
    }
  };

  const handleBulkRestore = async () => {
    if (isBulkBusy || selectedIds.size === 0) return;

    const ids = Array.from(selectedIds);
    const confirmado = window.confirm(
      `Reabilitar ${ids.length} publicação(ões) selecionada(s)?\n\nOs processos voltarão a poder ser sincronizados e as publicações serão desarquivadas.`,
    );
    if (!confirmado) return;

    setIsBulkRestoring(true);
    try {
      const result = await publicationsDjeService.bulkRestore(ids);
      const processedIds = result?.data?.processedIds || ids;
      const restoredCount =
        result?.data?.restoredProcesses?.length ?? processedIds.length;

      removeSelectedFromList(processedIds);
      clearSelection();
      closePublicationSidebar();

      alert(
        `${restoredCount} processo(s) reabilitado(s). Novas atualizações voltarão a ser sincronizadas.`,
      );
    } catch (error) {
      const apiMessage = error?.response?.data?.error;
      alert(apiMessage || "Erro ao reabilitar publicações. Tente novamente.");
    } finally {
      setIsBulkRestoring(false);
    }
  };

  const clearPublicationFromUrl = () => {
    if (!searchParams.has("id")) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("id");
    setSearchParams(nextParams);
  };

  const closePublicationSidebar = () => {
    setSidebarOpen(false);
    setQuickLawyerSource(null);
    setSelectedPublicacao(null);
    clearPublicationFromUrl();
  };

  const handleCloseSidebar = () => {
    if (selectedPublicacao && !selectedPublicacao.lida) {
      publicationsDjeService.markPublicationAsRead(selectedPublicacao.id);
    }

    closePublicationSidebar();
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Erro ao Carregar Publicações DJE
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Publicações {totalItems > 0 && `(${totalItems})`}
          </h1>
          <p className="text-gray-600">
            Monitoramento de publicações do Diário da Justiça Eletrônico
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw
            className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
          />
          Atualizar
        </button>
      </div>

      {/* Filtros e Busca */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-start gap-4">
          <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-0 w-full">
            <div className="flex-1 min-w-0">
              <CNJSearchInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onSubmit={handleSearch}
                placeholder="Buscar por processo, tribunal, órgão, classificação..."
                showCNJIndicator={true}
                showTooltip={false}
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex-shrink-0"
            >
              Buscar
            </button>
          </form>

          {/* Filtros rápidos */}
          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            {filtros.map((filtro) => (
              <button
                key={filtro.id}
                onClick={() => {
                  if (filtro.id !== selectedFilter) {
                    closePublicationSidebar();
                  }
                  setSelectedFilter(filtro.id);
                  setCurrentPage(1);
                }}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedFilter === filtro.id
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {filtro.label}
              </button>
            ))}

            {/* Botão de Filtros */}
            <button
              onClick={() => setFilterSidebarOpen(true)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                hasNonSearchFilters()
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <Filter className="w-4 h-4" />
              Filtros
              {countSidebarFilters() > 0 && (
                <span className="bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center">
                  {countSidebarFilters()}
                </span>
              )}
            </button>

            {hasActiveFilters() && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                title="Restaurar busca, status e filtros avançados ao padrão"
              >
                <Trash2 className="w-4 h-4" />
                Limpar Filtros
              </button>
            )}

            {/* Seletor de itens por página */}
            <PerPageSelector
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              options={[25, 50, 100, 200, 400, 800]}
            />
          </div>
        </div>

        {isSearchingByCNJ && (
          <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            Busca por número CNJ ativa — os demais filtros estão sendo
            ignorados.
          </p>
        )}
      </div>

      {/* Barra de ordenação e seleção */}
      {!loading && filteredPublicacoes.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <button
                onClick={toggleSelectAll}
                disabled={selectablePublicacoes.length === 0}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  selectablePublicacoes.length === 0
                    ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                title="Selecionar todas as publicações conversíveis desta página"
              >
                {allSelected ? (
                  <CheckSquare className="w-4 h-4 text-blue-600" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
                {allSelected ? "Desmarcar todos" : "Selecionar todos"}
              </button>
              {selectedIds.size > 0 && (
                <span className="text-xs font-medium text-blue-700">
                  {selectedIds.size} selecionada(s)
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm text-gray-600">
                Ordenar por:
              </span>
              <button
                onClick={() => handleSort("datePublication")}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  sortField === "datePublication"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                Data {getSortIcon("datePublication")}
              </button>
              <button
                onClick={() => handleSort("score")}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  sortField === "score"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                Score {getSortIcon("score")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barra de ação para operações em lote */}
      {selectedIds.size > 0 && (
        <div className="sticky top-2 z-20 bg-blue-600 text-white rounded-xl shadow-lg px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-medium">
            <CheckSquare className="w-5 h-5" />
            {selectedIds.size} publicação(ões) selecionada(s)
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={clearSelection}
              disabled={isBulkBusy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-500 hover:bg-blue-400 transition-colors disabled:opacity-50"
            >
              <X className="w-4 h-4" />
              Limpar
            </button>
            {selectedFilter === "descartado" ? (
              <button
                onClick={handleBulkRestore}
                disabled={isBulkBusy}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold bg-emerald-100 text-emerald-800 hover:bg-emerald-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                title="Reabilitar processos: remove do descarte e permite sincronização novamente"
              >
                {isBulkRestoring ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4" />
                )}
                {isBulkRestoring ? "Reabilitando..." : "Reabilitar"}
              </button>
            ) : selectedFilter === "arquivado" ? (
              <button
                onClick={handleBulkUnarchive}
                disabled={isBulkBusy}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold bg-emerald-100 text-emerald-800 hover:bg-emerald-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                title="Desarquivar as publicações selecionadas"
              >
                {isBulkUnarchiving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Archive className="w-4 h-4" />
                )}
                {isBulkUnarchiving ? "Desarquivando..." : "Desarquivar"}
              </button>
            ) : (
              <>
                <button
                  onClick={handleBulkArchive}
                  disabled={isBulkBusy}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  title="Arquivar as publicações selecionadas"
                >
                  {isBulkArchiving ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Archive className="w-4 h-4" />
                  )}
                  {isBulkArchiving ? "Arquivando..." : "Arquivar"}
                </button>
                <button
                  onClick={handleBulkDiscard}
                  disabled={isBulkBusy}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold bg-red-100 text-red-800 hover:bg-red-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  title="Descartar: arquiva e impede novas sincronizações destes processos"
                >
                  {isBulkDiscarding ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Ban className="w-4 h-4" />
                  )}
                  {isBulkDiscarding ? "Descartando..." : "Descartar"}
                </button>
                <button
                  onClick={handleBulkConvert}
                  disabled={isBulkBusy}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold bg-white text-blue-700 hover:bg-blue-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isBulkConverting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <ArrowRight className="w-4 h-4" />
                  )}
                  {isBulkConverting
                    ? "Convertendo..."
                    : "Enviar para processos"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Lista de Publicações em Cards */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
            <div className="flex items-center justify-center p-12">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">
                Carregando publicações...
              </span>
            </div>
          </div>
        ) : filteredPublicacoes.length > 0 ? (
          <>
            {filteredPublicacoes.map((publicacao) => (
              <div
                key={publicacao.id}
                onClick={() => handleViewDetails(publicacao)}
                className={`bg-white border rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden cursor-pointer group ${
                  selectedIds.has(publicacao.id)
                    ? "border-blue-500 ring-2 ring-blue-200"
                    : "border-gray-200 hover:border-blue-300"
                }`}
              >
                <div className="p-5">
                  {/* Header com Score */}
                  <div className="flex items-start justify-between mb-4">
                    {/* Checkbox de seleção */}
                    {isSelectablePublicacao(publicacao) ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleSelectPublicacao(publicacao.id);
                        }}
                        className="mr-3 mt-0.5 flex-shrink-0 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Selecionar publicação para conversão em lote"
                        aria-label="Selecionar publicação"
                      >
                        {selectedIds.has(publicacao.id) ? (
                          <CheckSquare className="w-5 h-5 text-blue-600" />
                        ) : (
                          <Square className="w-5 h-5" />
                        )}
                      </button>
                    ) : (
                      <span className="mr-3 mt-0.5 w-5 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      {/* Número do Processo */}
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-gray-400 flex-shrink-0 group-hover:text-blue-500 transition-colors" />
                        <ProcessNumber
                          processNumber={publicacao.numeroProcessoFormatted}
                          className="text-base font-semibold text-gray-900 group-hover:text-blue-600 transition-colors"
                          copyable={true}
                        />
                        {publicacao.convertedToProcess && (
                          <ConvertedToProcessBadge
                            processId={publicacao.processId}
                            processStatus={publicacao.processStatus}
                            compact
                          />
                        )}
                        {publicacao.descartada && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                            <Ban className="w-3 h-3" />
                            Descartada
                          </span>
                        )}
                      </div>
                      {/* Tribunal e Órgão */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                        <div className="flex items-center gap-1.5">
                          <Building2 className="w-3.5 h-3.5 text-gray-400" />
                          <span className="font-medium text-gray-700">
                            {publicacao.tribunal}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Scale className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-gray-600">
                            {publicacao.nomeOrgao}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-gray-600">
                            {publicacao.dataDisponibilizacao}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Score */}
                    <div className="flex items-center ml-4">
                      <div className="text-center">
                        {getScoreBadge(
                          publicacao.score,
                          publicacao.scoreBreakdown,
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Classe do Processo */}
                  {publicacao.nomeClasse && (
                    <div className="mb-3 pb-3 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Classe:
                        </span>
                        <span className="text-xs font-semibold text-gray-500">
                          {publicacao.nomeClasse}
                          {publicacao.codigoClasse &&
                            ` (${publicacao.codigoClasse})`}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Classificações */}
                  {publicacao.classifications &&
                    publicacao.classifications.length > 0 && (
                      <div className="mb-3 pb-3 border-b border-gray-100">
                        <div className="mb-2">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Classificações:
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {publicacao.classifications
                            .sort((a, b) => {
                              const priorityOrder = {
                                urgente: 1,
                                alta: 2,
                                média: 3,
                                media: 3,
                                normal: 4,
                                baixa: 5,
                              };

                              const aPriority =
                                priorityOrder[a.priority?.toLowerCase()] || 6;
                              const bPriority =
                                priorityOrder[b.priority?.toLowerCase()] || 6;

                              if (aPriority !== bPriority) {
                                return aPriority - bPriority;
                              }

                              return a.name.localeCompare(b.name);
                            })
                            .map((classification, idx) => {
                              const getPriorityColor = (priority) => {
                                switch (priority?.toLowerCase()) {
                                  case "urgente":
                                    return "bg-red-100 text-red-700 border-red-200";
                                  case "alta":
                                    return "bg-yellow-100 text-yellow-700 border-yellow-200";
                                  case "normal":
                                    return "bg-blue-100 text-blue-700 border-blue-200";
                                  case "baixa":
                                    return "bg-gray-100 text-gray-700 border-gray-200";
                                  default:
                                    return "bg-gray-100 text-gray-700 border-gray-200";
                                }
                              };

                              return (
                                <span
                                  key={idx}
                                  className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${getPriorityColor(
                                    classification.priority,
                                  )}`}
                                  title={`Prioridade: ${classification.priority}`}
                                >
                                  {classification.name}
                                </span>
                              );
                            })}
                        </div>
                      </div>
                    )}

                  {/* Partes do Processo */}
                  {(publicacao.displayParties?.activePoles?.length > 0 ||
                    publicacao.displayParties?.passivePoles?.length > 0) && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Partes:
                        </span>
                      </div>
                      <div className="space-y-2 ml-5">
                        {publicacao.displayParties?.activePoles?.length > 0 && (
                          <div className="flex items-start gap-2">
                            <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded">
                              Polo Ativo:
                            </span>
                            <span className="text-sm text-gray-900 flex-1">
                              {publicacao.displayParties.activePoles
                                .map((pole) => pole.name)
                                .join(", ")}
                            </span>
                          </div>
                        )}
                        {publicacao.displayParties?.passivePoles?.length >
                          0 && (
                          <div className="flex items-start gap-2">
                            <span className="text-xs font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded">
                              Polo Passivo:
                            </span>
                            <span className="text-sm text-gray-900 flex-1">
                              {publicacao.displayParties.passivePoles
                                .map((pole) => pole.name)
                                .join(", ")}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {publicacao.lawyerCandidates?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 my-2">
                        <Scale className="w-3.5 h-3.5 text-blue-400" />
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Advogado:
                        </span>
                      </div>
                      <div className="ml-5 rounded-lg border border-blue-100 bg-blue-50/60 p-3 space-y-2">
                        <p className="text-sm font-semibold text-gray-900">
                          {publicacao.lawyerCandidates[0].nome}
                        </p>
                        {publicacao.lawyerCandidates[0].oab ? (
                          <p className="text-xs text-gray-600">
                            OAB {publicacao.lawyerCandidates[0].oab}
                          </p>
                        ) : null}
                        {publicacao.matchedPartners?.length > 0 ? (
                          <PartnerLawyerBadge
                            partner={publicacao.matchedPartners[0]}
                            compact={true}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openQuickLawyerModal(
                                publicacao,
                                publicacao.lawyerCandidates[0],
                              );
                            }}
                            className="inline-flex items-center rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 transition hover:bg-blue-100"
                          >
                            Adicionar como parceiro
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhuma publicação encontrada
              </h3>
              <p className="text-gray-600">
                Tente ajustar os filtros de busca ou atualize a página.
              </p>
            </div>
          </div>
        )}

        {/* Paginação */}
        {!loading && totalPages > 1 && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-center">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                showInfo={false}
              />
            </div>
          </div>
        )}
      </div>

      <QuickLawyerPartnerModal
        open={quickLawyerModalOpen}
        onClose={() => setQuickLawyerModalOpen(false)}
        content={quickLawyerSource?.publication?.detalhes?.conteudo || ""}
        sourceLabel={`publicação DJE ${quickLawyerSource?.publication?.numeroProcesso || quickLawyerSource?.publication?.id || ""}`}
        initialLawyers={
          quickLawyerSource?.lawyerCandidate
            ? [quickLawyerSource.lawyerCandidate]
            : []
        }
        onCreated={handlePartnerCreated}
      />

      {/* Publication Details Sidebar */}
      <PublicationDjeDetailsSidebar
        publication={selectedPublicacao}
        isOpen={sidebarOpen}
        onClose={handleCloseSidebar}
        onPartnerCreated={handlePartnerCreated}
        onPublicationConverted={handlePublicationConverted}
        onPublicationReclassified={handlePublicationReclassified}
        onPublicationArchived={handlePublicationArchived}
        onPublicationUnarchived={handlePublicationUnarchived}
        onPublicationDiscarded={handlePublicationDiscarded}
        onPublicationRestored={handlePublicationRestored}
      />

      {/* Filter Sidebar */}
      <FilterSidebarDje
        isOpen={filterSidebarOpen}
        onClose={() => setFilterSidebarOpen(false)}
        selectedClassifications={selectedClassifications}
        setSelectedClassifications={setSelectedClassifications}
        availableClassifications={availableClassifications}
        classificationsLoading={classificationsLoading}
        selectedScoreRange={selectedScoreRange}
        setSelectedScoreRange={setSelectedScoreRange}
        selectedTribunal={selectedTribunal}
        setSelectedTribunal={setSelectedTribunal}
        availableTribunals={availableTribunals}
        tribunalsLoading={tribunalsLoading}
        selectedJusticeSphere={selectedJusticeSphere}
        setSelectedJusticeSphere={setSelectedJusticeSphere}
        availableJusticeSpheres={availableJusticeSpheres}
        justiceSpheresLoading={justiceSpheresLoading}
        selectedState={selectedState}
        setSelectedState={setSelectedState}
        availableStates={availableStates}
        statesLoading={statesLoading}
        selectedPoloPassivo={selectedPoloPassivo}
        setSelectedPoloPassivo={setSelectedPoloPassivo}
        selectedDateStart={selectedDateStart}
        setSelectedDateStart={setSelectedDateStart}
        selectedDateEnd={selectedDateEnd}
        setSelectedDateEnd={setSelectedDateEnd}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters()}
      />
    </div>
  );
}
